/* LQK games kit — shared helpers for every /pages game.
 *
 * Public API (window.LQKG) — the per-game scripts depend on every name here:
 *   shuffle, vibrate, sound{muted,toggle,tap,flip,pop,ding,buzz,win},
 *   confetti, modal, soundButton, timer, reducedMotion
 * Added by the 2026-08 remaster:
 *   preload(urls) -> Promise     fetches assets as blobs so a dropped
 *                                connection mid-game breaks nothing
 *   asset(url) -> string         blob URL if preloaded, else the original
 * Auto-wiring: a `.lqkg` root may carry
 *   data-preload='["//cdn/...a.png", ...]'       extra assets to pin
 *   data-win-art="//cdn/...win.png"              celebration art in the modal
 * Games whose sections predate this kit (arcade / listen / adventure, added to
 * the store 2026-08-05) carry no data-win-art, so GAME_DEFAULTS below supplies
 * one from the root's marker attribute — no section edit required.
 * There is deliberately NO background music: the games ship SFX only.
 * The kit also pins every <img src> and background-image already inside the
 * .lqkg root, and rewrites them to blob URLs as they appear (MutationObserver),
 * so per-game scripts never need to know about caching.
 */
(function () {
  'use strict';
  if (window.LQKG) return;
  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function shuffle(arr) {
    for (var a = arr.slice(), i = a.length - 1; i > 0; i--) {
      var j = Math.floor(Math.random() * (i + 1)), t = a[i];
      a[i] = a[j]; a[j] = t;
    }
    return a;
  }
  function vibrate(ms) { try { navigator.vibrate && navigator.vibrate(ms); } catch (e) {} }

  /* ── audio core ─────────────────────────────────────────────── */
  var MUTE_KEY = 'lqkg-muted', audioCtx = null;
  function ctx() {
    if (!audioCtx) {
      var AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return null;
      audioCtx = new AC();
    }
    if (audioCtx.state === 'suspended') audioCtx.resume();
    return audioCtx;
  }
  function muted() { try { return localStorage.getItem(MUTE_KEY) === '1'; } catch (e) { return false; } }
  function setMuted(v) { try { localStorage.setItem(MUTE_KEY, v ? '1' : '0'); } catch (e) {} }

  function tone(freq, dur, type, gainPeak, when) {
    var c = ctx();
    if (!c || muted()) return;
    var t0 = c.currentTime + (when || 0), osc = c.createOscillator(), g = c.createGain();
    osc.type = type || 'sine';
    osc.frequency.setValueAtTime(freq, t0);
    g.gain.setValueAtTime(1e-4, t0);
    g.gain.exponentialRampToValueAtTime(gainPeak || 0.18, t0 + 0.012);
    g.gain.exponentialRampToValueAtTime(1e-4, t0 + dur);
    osc.connect(g).connect(c.destination);
    osc.start(t0); osc.stop(t0 + dur + 0.05);
  }
  var sound = {
    muted: muted,
    toggle: function () {
      setMuted(!muted());
      // the win fanfare is the only thing that can be playing under the button
      if (muted()) celebration.stop();
      else if (document.querySelector('.lqkg__overlay[data-open="true"]')) celebration.start();
      return muted();
    },
    tap: function () { tone(660, 0.08, 'sine', 0.12); },
    flip: function () { tone(520, 0.09, 'triangle', 0.1); },
    pop: function () { tone(880, 0.07, 'square', 0.08); tone(1320, 0.05, 'sine', 0.06, 0.02); },
    ding: function () { tone(880, 0.18, 'sine', 0.16); tone(1318.5, 0.22, 'sine', 0.1, 0.06); },
    buzz: function () { tone(180, 0.16, 'sawtooth', 0.06); },
    win: function () {
      [523.25, 659.25, 783.99, 1046.5].forEach(function (f, i) { tone(f, 0.22, 'triangle', 0.14, i * 0.11); });
    },
  };

  /* ── celebration fanfare ────────────────────────────────────────
   * The ONLY music in the games: a short congratulatory loop that plays
   * while a win summary is open, and stops the moment it closes. Obeys the
   * same mute button as the SFX. */
  var celebration = (function () {
    // C major, two bars of melody over a warm pad — deliberately bright.
    var MELODY = [
      [523.25, 0], [659.25, 0.18], [783.99, 0.36], [1046.5, 0.54],
      [987.77, 0.9], [783.99, 1.08], [880.0, 1.26], [1046.5, 1.44],
      [1174.66, 1.8], [1046.5, 1.98], [783.99, 2.16], [1046.5, 2.34],
      [1318.51, 2.7],
    ];
    var PAD = [[261.63, 0, 1.7], [349.23, 1.8, 0.8], [392.0, 2.7, 1.2]];
    var LOOP = 4.2;
    var bus = null, playing = false, timerId = null, nextAt = 0;
    function bus_() {
      var c = ctx();
      if (!c) return null;
      if (!bus) { bus = c.createGain(); bus.gain.value = 1; bus.connect(c.destination); }
      return bus;
    }
    function note(freq, t0, dur, type, peak) {
      var c = ctx(), b = bus_();
      if (!c || !b) return;
      var o = c.createOscillator(), g = c.createGain();
      o.type = type; o.frequency.setValueAtTime(freq, t0);
      g.gain.setValueAtTime(1e-4, t0);
      g.gain.linearRampToValueAtTime(peak, t0 + 0.03);
      g.gain.exponentialRampToValueAtTime(1e-4, t0 + dur);
      o.connect(g).connect(b);
      o.start(t0); o.stop(t0 + dur + 0.08);
    }
    function scheduleRound(t0) {
      MELODY.forEach(function (n) { note(n[0], t0 + n[1], 0.34, 'triangle', 0.12); });
      PAD.forEach(function (n) { note(n[0], t0 + n[1], n[2], 'sine', 0.05); });
    }
    function loop() {
      if (!playing) return;
      var c = ctx();
      if (!c) return;
      while (nextAt < c.currentTime + LOOP * 1.2) {
        scheduleRound(Math.max(nextAt, c.currentTime + 0.05));
        nextAt = Math.max(nextAt, c.currentTime + 0.05) + LOOP;
      }
      timerId = setTimeout(loop, LOOP * 500);
    }
    return {
      start: function () {
        if (playing || muted()) return;
        var c = ctx();
        if (!c) return;
        playing = true; nextAt = c.currentTime + 0.05;
        loop();
      },
      stop: function () {
        playing = false;
        clearTimeout(timerId);
        if (bus && audioCtx) {
          var t = audioCtx.currentTime;
          bus.gain.cancelScheduledValues(t);
          bus.gain.setValueAtTime(bus.gain.value, t);
          bus.gain.linearRampToValueAtTime(1e-4, t + 0.18);
          setTimeout(function () { if (!playing && bus) bus.gain.value = 1; }, 320);
        }
      },
      playing: function () { return playing; },
    };
  })();

  /* ── player name ────────────────────────────────────────────────
   * Typed once in a win summary, remembered across every game on the
   * store. Kept local to the device — nothing is sent anywhere. */
  var NAME_KEY = 'lqkg-name';
  var player = {
    get: function () {
      try { return (localStorage.getItem(NAME_KEY) || '').slice(0, 24); } catch (e) { return ''; }
    },
    set: function (v) {
      v = (v || '').replace(/\s+/g, ' ').trim().slice(0, 24);
      try { v ? localStorage.setItem(NAME_KEY, v) : localStorage.removeItem(NAME_KEY); } catch (e) {}
      return v;
    },
  };

  /* ── asset preloader / offline pin ──────────────────────────────
   * Fetch each URL once, hold it as a blob URL, and rewrite any element
   * inside .lqkg roots that references the original URL. After preload
   * completes the game runs entirely from memory. */
  var blobMap = {}; // absolute URL -> blob URL
  function absolute(u) {
    if (!u) return u;
    var a = document.createElement('a');
    a.href = u;
    return a.href;
  }
  function asset(url) { return blobMap[absolute(url)] || url; }
  function pin(url) {
    var abs = absolute(url);
    if (blobMap[abs]) return Promise.resolve();
    return fetch(abs, { mode: 'cors' })
      .then(function (r) { if (!r.ok) throw new Error(r.status); return r.blob(); })
      .then(function (b) { blobMap[abs] = URL.createObjectURL(b); })
      .catch(function () { /* stays un-pinned; the original URL keeps working */ });
  }
  function swapEl(el) {
    if (el.tagName === 'IMG') {
      var abs = absolute(el.getAttribute('src'));
      if (blobMap[abs] && el.src !== blobMap[abs]) el.src = blobMap[abs];
    }
    var bg = el.style && el.style.backgroundImage;
    if (bg) {
      var m = bg.match(/url\((['"]?)(.*?)\1\)/);
      if (m) {
        var abs2 = absolute(m[2]);
        if (blobMap[abs2] && m[2] !== blobMap[abs2]) el.style.backgroundImage = "url('" + blobMap[abs2] + "')";
      }
    }
  }
  function swapTree(root) {
    swapEl(root);
    root.querySelectorAll('img,[style*="background-image"]').forEach(swapEl);
  }
  function watch(root) {
    new MutationObserver(function (muts) {
      muts.forEach(function (mu) {
        if (mu.type === 'attributes') swapEl(mu.target);
        mu.addedNodes && mu.addedNodes.forEach(function (n) { if (n.nodeType === 1) swapTree(n); });
      });
    }).observe(root, { subtree: true, childList: true, attributes: true, attributeFilter: ['src', 'style'] });
  }
  function preload(urls) {
    return Promise.all((urls || []).map(pin));
  }

  /* ── confetti (unchanged) ───────────────────────────────────── */
  var COLORS = ['#e0a93b', '#333a22', '#dfc591', '#cfe0e3', '#f0d9d2', '#d6e4d0'];
  function confetti(opts) {
    if (reduced) return;
    opts = opts || {};
    var canvas = document.createElement('canvas');
    canvas.className = 'lqkg__confetti';
    document.body.appendChild(canvas);
    var g = canvas.getContext('2d'), dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = innerWidth * dpr; canvas.height = innerHeight * dpr; g.scale(dpr, dpr);
    for (var n = opts.count || 130, parts = [], i = 0; i < n; i++) {
      parts.push({
        x: innerWidth * (0.2 + Math.random() * 0.6), y: -20 - Math.random() * innerHeight * 0.25,
        w: 6 + Math.random() * 6, h: 8 + Math.random() * 8,
        vx: (Math.random() - 0.5) * 2.4, vy: 2 + Math.random() * 2.6,
        rot: Math.random() * Math.PI, vr: (Math.random() - 0.5) * 0.24,
        color: COLORS[i % COLORS.length],
      });
    }
    var t0 = performance.now();
    (function frame(t) {
      var elapsed = t - t0;
      g.clearRect(0, 0, innerWidth, innerHeight);
      var alive = false;
      parts.forEach(function (p) {
        p.x += p.vx; p.y += p.vy; p.rot += p.vr;
        if (p.y < innerHeight + 30) alive = true;
        g.save(); g.translate(p.x, p.y); g.rotate(p.rot);
        g.fillStyle = p.color; g.fillRect(-p.w / 2, -p.h / 2, p.w, p.h); g.restore();
      });
      if (alive && elapsed < 3800) requestAnimationFrame(frame); else canvas.remove();
    })(t0);
  }

  /* ── win modal (unchanged API; art + name + fanfare added) ───── */
  function modal(root) {
    root = root || document.querySelector('.lqkg') || document.body;
    var starImg = '<img class="lqkg__star" src="' + themeAsset('lqk-g-ui-star.png') + '" alt="" aria-hidden="true">';
    var overlay = document.createElement('div');
    overlay.className = 'lqkg__overlay';
    overlay.setAttribute('data-open', 'false');
    overlay.innerHTML =
      '<div class="lqkg__modal" role="dialog" aria-modal="true" aria-labelledby="lqkg-modal-title">' +
      '<div class="lqkg__modal-art" data-art hidden><img alt="" loading="lazy"></div>' +
      '<div class="lqkg__stars" aria-hidden="true">' + starImg + starImg + starImg + '</div>' +
      '<h3 id="lqkg-modal-title"></h3><p class="lqkg__modal-text"></p>' +
      '<div class="lqkg__namebox">' +
      '<label class="lqkg__namelabel" for="lqkg-name-input">Who played?</label>' +
      '<input class="lqkg__nameinput" id="lqkg-name-input" type="text" maxlength="24" autocomplete="off"' +
      ' spellcheck="false" placeholder="Type your name">' +
      '</div>' +
      '<div class="lqkg__modal-actions"><button type="button" class="lqkg__btn" data-act="again"></button>' +
      '<a class="lqkg__btn lqkg__btn--ghost" data-act="all" href="/pages/lqk-games">All games</a></div></div>';
    root.appendChild(overlay);
    var titleEl = overlay.querySelector('h3'), textEl = overlay.querySelector('p'),
      againBtn = overlay.querySelector('[data-act="again"]'),
      artBox = overlay.querySelector('[data-art]'), artImg = artBox.querySelector('img'),
      nameInput = overlay.querySelector('.lqkg__nameinput'),
      stars = overlay.querySelectorAll('.lqkg__star'), onAgain = null, baseTitle = '';

    // The heading greets whoever is typing, live, so a child sees their own
    // name land in the summary the moment they enter it.
    function paintTitle() {
      var who = player.get();
      titleEl.textContent = who ? who + ', ' + baseTitle.charAt(0).toLowerCase() + baseTitle.slice(1) : baseTitle;
    }
    nameInput.addEventListener('input', function () { player.set(nameInput.value); paintTitle(); });
    nameInput.addEventListener('blur', function () { nameInput.value = player.get(); paintTitle(); });
    againBtn.addEventListener('click', function () { hide(); onAgain && onAgain(); });

    function show(cfg) {
      baseTitle = cfg.title || 'Mumtaz! ⭐';
      nameInput.value = player.get();
      paintTitle();
      textEl.textContent = cfg.text || '';
      againBtn.textContent = cfg.againLabel || 'Play again';
      onAgain = cfg.onAgain || null;
      // per-game default celebration art, supplied by the section markup so
      // existing game scripts get it without any code change
      var art = cfg.art || (root.getAttribute && root.getAttribute('data-win-art'));
      if (art) { artImg.src = asset(art); artBox.hidden = false; } else { artBox.hidden = true; }
      var lit = typeof cfg.stars === 'number' ? cfg.stars : 3;
      stars.forEach(function (s, i) { s.classList.toggle('is-lit', i < lit); });
      overlay.setAttribute('data-open', 'true');
      celebration.start();
    }
    function hide() { overlay.setAttribute('data-open', 'false'); celebration.stop(); }
    return { show: show, hide: hide };
  }

  /* ── sound button (unchanged) ───────────────────────────────── */
  var SPEAKER_ON = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M11 5 6 9H2v6h4l5 4z"/><path d="M15.5 8.5a5 5 0 0 1 0 7"/><path d="M18.5 5.5a9 9 0 0 1 0 13"/></svg>';
  var SPEAKER_OFF = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M11 5 6 9H2v6h4l5 4z"/><line x1="22" y1="9" x2="16" y2="15"/><line x1="16" y1="9" x2="22" y2="15"/></svg>';
  function soundButton(btn) {
    function render() {
      var m = sound.muted();
      btn.innerHTML = m ? SPEAKER_OFF : SPEAKER_ON;
      btn.setAttribute('aria-pressed', m ? 'true' : 'false');
      btn.setAttribute('aria-label', m ? 'Turn sound on' : 'Turn sound off');
    }
    btn.addEventListener('click', function () { sound.toggle(); render(); if (!sound.muted()) sound.tap(); });
    render();
  }

  /* ── timer (unchanged) ──────────────────────────────────────── */
  function timer(el) {
    var start = null, raf = null, running = false, frozen = 0;
    function fmt(ms) { var s = Math.floor(ms / 1e3); return Math.floor(s / 60) + ':' + ('0' + (s % 60)).slice(-2); }
    function tick() { if (running) { el.textContent = fmt(performance.now() - start); raf = setTimeout(tick, 500); } }
    return {
      start: function () { start = performance.now(); running = true; tick(); },
      stop: function () { running = false; clearTimeout(raf); frozen = start ? performance.now() - start : 0; return frozen; },
      reset: function () { running = false; clearTimeout(raf); el.textContent = '0:00'; },
      seconds: function () { return Math.round((running ? performance.now() - start : frozen) / 1e3); },
    };
  }

  /* ── auto-wiring ────────────────────────────────────────────── */
  // Per-game fallbacks for sections this remaster does not ship. Keyed on the
  // marker attribute each game script looks for.
  var GAME_DEFAULTS = {
    'data-lqkg-arcade': { winArt: 'lqk-g-win-generic.png' },
    'data-lqkg-adventure': { winArt: 'lqk-g-win-generic.png' },
    'data-lqkg-listen': { winArt: 'lqk-g-win-generic.png' },
    'data-lqkg-daily': { winArt: 'lqk-g-win-generic.png' },
  };
  // Theme assets all live in one flat directory, so the kit's own URL locates
  // any sibling asset without Liquid.
  var kitSrc = (document.currentScript && document.currentScript.src) || (function () {
    var s = document.querySelectorAll('script[src*="lqk-games-kit"]');
    return s.length ? s[s.length - 1].src : '';
  })();
  function themeAsset(name) {
    return /lqk-games-kit\.js/.test(kitSrc) ? kitSrc.replace(/lqk-games-kit\.js.*$/, name) : name;
  }
  // 3D icon art in place of emoji/vector glyphs. icon('heart') -> <img …>
  function iconUrl(name) { return themeAsset('lqk-g-ui-' + name + '.png'); }
  function icon(name, cls) {
    return '<img class="lqkg__icon' + (cls ? ' ' + cls : '') + '" src="' + iconUrl(name) + '" alt="" aria-hidden="true">';
  }
  // pastel plate art for the letter/number grids, exposed to CSS as
  // --lqkg-plate-1..6 so every game (including sections we don't ship) gets it
  var PLATES = ['mint', 'blush', 'sky', 'lemon', 'lavender', 'peach'];
  // Emoji still baked into section markup we don't ship (e.g. the adventure
  // "⭐ Stars" chip). Only the chips' own text nodes are touched — never the
  // <b> counters the games rewrite.
  var CHIP_EMOJI = { '⭐': 'star', '🌙': 'moon', '🌹': 'rose', '🌸': 'blossom', '💡': 'bulb', '♥': 'heart', '🔥': 'flame' };
  function swapChipEmoji(root) {
    root.querySelectorAll('.lqkg__chip').forEach(function (chip) {
      chip.childNodes.forEach(function (node) {
        if (node.nodeType !== 3) return;
        var key = Object.keys(CHIP_EMOJI).filter(function (e) { return node.nodeValue.indexOf(e) > -1; })[0];
        if (!key) return;
        var span = document.createElement('span');
        span.innerHTML = node.nodeValue.split(key).join(icon(CHIP_EMOJI[key]));
        chip.replaceChild(span, node);
      });
    });
  }

  function applyDefaults(root, urls) {
    PLATES.forEach(function (name, i) {
      var url = themeAsset('lqk-g-plate-' + name + '.png');
      root.style.setProperty('--lqkg-plate-' + (i + 1), "url('" + url + "')");
      urls.push(url);
    });
    root.style.setProperty('--lqkg-lock', "url('" + iconUrl('lock') + "')");
    urls.push(iconUrl('lock'), iconUrl('star'));
    for (var attr in GAME_DEFAULTS) {
      if (!root.hasAttribute(attr)) continue;
      var d = GAME_DEFAULTS[attr];
      if (d.winArt && !root.getAttribute('data-win-art')) {
        var url = themeAsset(d.winArt);
        root.setAttribute('data-win-art', url);
        urls.push(url);
      }
    }
  }

  function init() {
    document.querySelectorAll('.lqkg').forEach(function (root) {
      watch(root);
      var urls = [];
      applyDefaults(root, urls);
      swapChipEmoji(root);
      var extra = root.getAttribute('data-preload');
      if (extra) { try { urls = urls.concat(JSON.parse(extra)); } catch (e) {} }
      root.querySelectorAll('img[src]').forEach(function (im) { urls.push(im.getAttribute('src')); });
      root.querySelectorAll('[style*="background-image"]').forEach(function (el) {
        var m = (el.getAttribute('style') || '').match(/url\((['"]?)(.*?)\1\)/);
        if (m) urls.push(m[2]);
      });
      // per-game data attributes that carry image URLs, in any of three shapes:
      // ["url", ...] | [{src|img: "url"}, ...] | {"key": "url", ...}
      ['data-cards', 'data-scenes', 'data-plants', 'data-tasks', 'data-sprites', 'data-balloons'].forEach(function (attr) {
        var raw = root.getAttribute(attr);
        if (!raw) return;
        try {
          var data = JSON.parse(raw);
          (Array.isArray(data) ? data : Object.keys(data).map(function (k) { return data[k]; })).forEach(function (it) {
            if (typeof it === 'string') urls.push(it);
            else if (it && (it.src || it.img)) urls.push(it.src || it.img);
          });
        } catch (e) {}
      });
      preload(urls).then(function () { swapTree(root); });
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();

  window.LQKG = {
    shuffle: shuffle, vibrate: vibrate, sound: sound, confetti: confetti,
    modal: modal, soundButton: soundButton, timer: timer, reducedMotion: reduced,
    preload: preload, asset: asset, icon: icon, iconUrl: iconUrl, player: player,
    celebration: celebration,
  };
})();
